import { afterNextRender, ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { InventoryCheck } from '@helper/inventory-check';

interface IDialogData {
  count: number;
  max: number;
  min: number;
  remindExceededMax: boolean;
}

@Component({
  selector: 'app-number-picker-dialog',
  templateUrl: './number-picker-dialog.component.html',
  styleUrls: ['./number-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FocusRemoverDirective, ImageLoadingComponent, MatDialogClose],
})
export class NumberPickerDialogComponent {
  readonly data: IDialogData = inject(MAT_DIALOG_DATA);
  readonly value = signal(1);

  constructor(
    private dialogRef: MatDialogRef<NumberPickerDialogComponent, number | undefined>,
    private toast: MatSnackBar
  ) {
    afterNextRender(() => this.value.set(this.data.count));
  }

  static open(
    dialog: MatDialog,
    count: number,
    max: number,
    min: number,
    remindExceededMax = false
  ): MatDialogRef<NumberPickerDialogComponent, number | undefined> {
    const data: IDialogData = { count, max, min, remindExceededMax };
    return dialog.open(NumberPickerDialogComponent, {
      disableClose: true,
      width: '250px',
      data,
    });
  }

  onClose(): void {
    this.dialogRef.close(this.value());
  }

  decrease(): void {
    if (this.value() <= this.data.min) {
      return;
    }
    this.value.update(v => v - 1);
  }

  increase(): void {
    if (this.data.remindExceededMax) {
      InventoryCheck.showExceededToast(this.toast, this.value() + 1, this.data.max);
    }
    if (this.value() >= this.data.max) {
      return;
    }
    this.value.update(v => v + 1);
  }

  onNumberChange(value: string, target: HTMLInputElement): void {
    const num = Number(value);
    if (!num) {
      target.value = this.value().toString();
      return;
    }
    if (this.data.remindExceededMax) {
      InventoryCheck.showExceededToast(this.toast, num, this.data.max);
    }
    this.value.set(Math.max(this.data.min, Math.min(this.data.max, num)));
    target.value = this.value().toString();
  }
}
